import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { topAndTail, formatDateTime, formatTime } from '../utils/stringUtils';
import { generateSchemaFromPayload, extractAgentIdentitySchema } from '../utils/schemaUtils';

const ChannelCapturePage: React.FC = () => {
  const { configId } = useParams<{ configId: string }>();
  const { state, actions, getCurrentStats } = useApp();
  const navigate = useNavigate();
  const [capturedPayloads, setCapturedPayloads] = useState<Array<{
    timestamp: Date;
    payload: any;
    response_payload?: any;
    outbound_request?: any;
    inbound_response?: any;
    channel: string;
    validation_status: string;
    validation_error?: string;
  }>>([]);
  const [selectedCapture, setSelectedCapture] = useState<{
    timestamp: Date;
    payload: any;
    response_payload?: any;
    outbound_request?: any;
    inbound_response?: any;
    channel: string;
    validation_status: string;
    validation_error?: string;
  } | null>(null);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const [latestInboundRequest, setLatestInboundRequest] = useState<string>('');
  const [latestOutboundRequest, setLatestOutboundRequest] = useState<string>('');
  const [latestInboundResponse, setLatestInboundResponse] = useState<string>('');
  const [latestOutboundResponse, setLatestOutboundResponse] = useState<string>('');
  const [latestValidationStatus, setLatestValidationStatus] = useState<string>('success');
  const [latestValidationError, setLatestValidationError] = useState<string | undefined>(undefined);
  const [payloadCount, setPayloadCount] = useState(0);
  const [displayedCapture, setDisplayedCapture] = useState<{
    timestamp: Date;
    channel: string;
  } | null>(null);
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [generatedSchema, setGeneratedSchema] = useState<string>('');
  const [schemaTitle, setSchemaTitle] = useState<string>('');
  const [schemaSourcePayload, setSchemaSourcePayload] = useState<any>(null);
  const [routingConfig, setRoutingConfig] = useState<{ available_listen_addresses: string[]; channel_path_prefix: Array<{id: string; name: string; prefix: string}> } | null>(null);

  // Find the channel by config_id from current stats
  const stats = getCurrentStats();
  const channel = stats?.channels?.find(r => r.config_id === configId);
  const isFabricChannel = channel?.target_endpoint?.startsWith('fabric://');
  
  // Compute full route for display - similar to EditChannelPage
  const fullRouteDisplay = (() => {
    if (!channel || !routingConfig) return '';
    
    // Find the matching listen address with scheme from routing config
    let hostPort = channel.listen_address;
    if (channel.listen_address.includes('/')) {
      hostPort = channel.listen_address.split('/')[0];
    }
    
    const matchingAddress = routingConfig.available_listen_addresses.find(addr => {
      const addrWithoutProtocol = addr.replace(/^https?:\/\//, '');
      return addrWithoutProtocol === hostPort || addrWithoutProtocol.startsWith(hostPort + ':');
    });
    
    const fullAddress = matchingAddress || channel.listen_address;
    const route = channel.route || '';
    
    return `${fullAddress}${route}`;
  })();
  
  // Determine the identity field name based on channel protocol
  const identityFieldName = channel?.protocol === 'mcp' 
    ? (channel?.mcp_identity_config?.meta_field || 'agentIdentity')
    : 'agentIdentity';

  // Fetch routing configuration
  useEffect(() => {
    const fetchRoutingConfig = async () => {
      try {
        const response = await fetch('/api/v1/config/channel-routing');
        if (!response.ok) {
          throw new Error('Failed to fetch routing configuration');
        }
        const config = await response.json();
        setRoutingConfig(config);
      } catch (error) {
        console.error('Failed to fetch routing configuration:', error);
      }
    };
    fetchRoutingConfig();
  }, []);

  useEffect(() => {
    if (!channel) {
      navigate('/channels');
      return;
    }



    // Start capturing when component mounts
    setIsCapturing(true);

    // Set up WebSocket listener for payload capture events
    // Use custom event with pre-parsed message to avoid redundant JSON parsing
    const handleWebSocketMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      
      // Only capture payloads for this specific channel being edited - filter by config_id
      if (data.type === 'payload_captured' && data.config_id === configId) {
          // Only process and update if not paused
          if (!isPausedRef.current) {
            const newPayload = {
              timestamp: new Date(data.timestamp || new Date()),
              payload: data.payload,
              response_payload: data.response_payload,
              outbound_request: data.outbound_request,
              inbound_response: data.inbound_response,
              channel: data.channel,
              validation_status: data.validation_status || "success",
              validation_error: data.validation_error
            };

            setCapturedPayloads(prev => [newPayload, ...prev.slice(0, 9)]); // Keep last 10 payloads
            setPayloadCount(prev => prev + 1);
            setLatestInboundRequest(JSON.stringify(data.payload, null, 2));
            setLatestOutboundRequest(data.outbound_request ? JSON.stringify(data.outbound_request, null, 2) : '');
            setLatestInboundResponse(data.inbound_response ? JSON.stringify(data.inbound_response, null, 2) : '');
            setLatestOutboundResponse(data.response_payload ? JSON.stringify(data.response_payload, null, 2) : '');
            setLatestValidationStatus(data.validation_status || "success");
            setLatestValidationError(data.validation_error);
            setDisplayedCapture({
              timestamp: newPayload.timestamp,
              channel: newPayload.channel
            });
          }
        }
    };

    // Listen to custom event instead of raw WebSocket message
    window.addEventListener('ws-message', handleWebSocketMessage);

    return () => {
      window.removeEventListener('ws-message', handleWebSocketMessage);
      setIsCapturing(false);
      document.body.classList.remove('modal-open');
    };
  }, [channel, state.wsConnection, state.wsStatus, navigate]);

  const clearPayloads = () => {
    setCapturedPayloads([]);
    setLatestInboundRequest('');
    setLatestOutboundRequest('');
    setLatestInboundResponse('');
    setLatestOutboundResponse('');
    setLatestValidationStatus('success');
    setLatestValidationError(undefined);
    setPayloadCount(0);
  };

  const loadCaptureIntoView = (capture: typeof capturedPayloads[0]) => {
    setLatestInboundRequest(JSON.stringify(capture.payload, null, 2));
    setLatestOutboundRequest(capture.outbound_request ? JSON.stringify(capture.outbound_request, null, 2) : '');
    setLatestInboundResponse(capture.inbound_response ? JSON.stringify(capture.inbound_response, null, 2) : '');
    setLatestOutboundResponse(capture.response_payload ? JSON.stringify(capture.response_payload, null, 2) : '');
    setLatestValidationStatus(capture.validation_status);
    setLatestValidationError(capture.validation_error);
    setDisplayedCapture({
      timestamp: capture.timestamp,
      channel: capture.channel
    });
    // Pause live updates when viewing historical capture
    setIsPaused(true);
  };

  const openCaptureModal = (capture: typeof capturedPayloads[0]) => {
    setSelectedCapture(capture);
    setShowCaptureModal(true);
    document.body.classList.add('modal-open');
  };

  const closeCaptureModal = () => {
    setSelectedCapture(null);
    setShowCaptureModal(false);
    document.body.classList.remove('modal-open');
  };

  const generateAndShowSchema = (payload: any, title: string) => {
    const schema = generateSchemaFromPayload(payload);
    setGeneratedSchema(schema);
    setSchemaTitle(title);
    setSchemaSourcePayload(payload);
    setShowSchemaModal(true);
    document.body.classList.add('modal-open');
  };

  const closeSchemaModal = () => {
    setShowSchemaModal(false);
    setGeneratedSchema('');
    setSchemaTitle('');
    setSchemaSourcePayload(null);
    document.body.classList.remove('modal-open');
  };

  const openLatestPayloadModal = () => {
    if (capturedPayloads.length > 0) {
      setSelectedCapture(capturedPayloads[0]);
      setShowCaptureModal(true);
      document.body.classList.add('modal-open');
    }
  };

  const copyToClipboard = async () => {
    if (latestInboundRequest) {
      try {
        const allStages = [
          '=== Inbound Request (from source agent) ===',
          latestInboundRequest,
          latestOutboundRequest ? '\n=== Outbound (to target) ===' : '',
          latestOutboundRequest || '',
          latestInboundResponse ? '\n=== Inbound Response (from target) ===' : '',
          latestInboundResponse || '',
          latestOutboundResponse ? '\n=== Outbound Result (to source agent) ===' : '',
          latestOutboundResponse || ''
        ].filter(s => s).join('\n');
        await navigator.clipboard.writeText(allStages);
        // Could add a toast notification here
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }
  };

  if (!channel) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle"></i> Channel not found.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div className="d-flex gap-2">
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate(`/channels/${encodeURIComponent(configId!)}`)}
          >
            <i className="fas fa-arrow-left"></i> Back to Channel
          </button>
        </div>
        <div className="d-flex gap-2">
          <button             className={`btn btn-sm ${isPaused ? 'btn-success' : 'btn-warning'}`}
            onClick={() => {
              const newPausedState = !isPaused;
              setIsPaused(newPausedState);
              isPausedRef.current = newPausedState;
              // When resuming, clear the displayed capture to show "Most Recent" again
              if (isPaused) {
                setDisplayedCapture(null);
              }
            }}
            title={isPaused ? 'Resume live capture' : 'Pause live capture'}
          >
            <i className={`fas ${isPaused ? 'fa-play' : 'fa-pause'}`}></i> {isPaused ? 'Resume' : 'Pause'}
          </button>
          <button             className="btn btn-sm btn-warning"
            onClick={clearPayloads}
            disabled={payloadCount === 0}
          >
            <i className="fas fa-trash"></i> Clear Payloads
          </button>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={copyToClipboard}
            disabled={!latestInboundRequest}
            title="Copy all stages to clipboard"
          >
            <i className="fas fa-copy"></i> Copy All Stages
          </button>
        </div>
      </div>

      {/* Channel Info Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-eye"></i> Channel Payload Capture
          </h6>
        </div>
        <div className="card-body">
          {/* Listener Info */}
          <div className="alert alert-success mb-3" style={{ fontSize: '14px' }}>
            <i className="fas fa-info-circle mr-2"></i><strong>Channel Route:</strong> {fullRouteDisplay}
            <button
              type="button"
              className="btn btn-xs ml-2"
              style={{ padding: '2px 6px', fontSize: '11px', outline: 'none' }}
              onClick={(e) => {
                navigator.clipboard.writeText(fullRouteDisplay);
                const btn = e.currentTarget as HTMLButtonElement;
                const originalHtml = btn.innerHTML;
                btn.innerHTML = '<i class="fas fa-check"></i>';
                setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
              }}
              title="Copy to clipboard"
            >
              <i className="fas fa-copy"></i>
            </button>
          </div>

          {/* Target Info */}
          <div className="alert alert-info mb-3" style={{ fontSize: '14px' }}>
            <div className="mb-2">
              <i className="fas fa-route mr-2"></i>
              <strong>Target: </strong> {channel.target_endpoint}
              <button
                type="button"
                className="btn btn-xs ml-2"
                style={{ padding: '2px 6px', fontSize: '11px', outline: 'none' }}
                onClick={(e) => {
                  navigator.clipboard.writeText(channel.target_endpoint);
                  const btn = e.currentTarget as HTMLButtonElement;
                  const originalHtml = btn.innerHTML;
                  btn.innerHTML = '<i class="fas fa-check"></i>';
                  setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
                }}
                title="Copy to clipboard"
              >
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <strong>Status:</strong> 
              <span className={`badge ml-2 ${isPaused ? 'badge-warning' : (isCapturing ? 'badge-success' : 'badge-secondary')}`}>
                {isPaused ? (
                  <>
                    <i className="fas fa-pause-circle"></i> Paused
                  </>
                ) : isCapturing ? (
                  <>
                    <i className="fas fa-circle pulse-icon"></i> Live Capturing
                  </>
                ) : (
                  <>
                    <i className="fas fa-stop-circle"></i> Stopped
                  </>
                )}
              </span>
            </div>
            <div className="col-md-4">
              <strong>Payloads Captured:</strong> 
              <span className="badge badge-info ml-2">{payloadCount}</span>
            </div>
            <div className="col-md-4">
              <strong>WebSocket:</strong> 
              <span className={`badge ml-2 ${state.wsStatus === 'CONNECTED' ? 'badge-success' : 'badge-warning'}`}>
                {state.wsStatus}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Latest Payload Display */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h6 className="m-0 font-weight-bold text-success mr-3">
                <i className="fas fa-file-code"></i> {displayedCapture ? (
                  <>
                    {displayedCapture.channel} - {formatDateTime(displayedCapture.timestamp, true)}
                    {isPaused && <span className="badge badge-warning ml-2">Historical</span>}
                  </>
                ) : (
                  'Most Recent Agent Payload'
                )}
              </h6>
              {latestInboundRequest && (
                <span 
                  className={`badge badge-pill cursor-pointer ${
                    latestValidationStatus === 'success' ? 'badge-success' : 
                    latestValidationStatus === 'Failed Validation' ? 'badge-danger' :
                    'badge-info'
                  }`}
                  onClick={openLatestPayloadModal}
                  title="Click to view details"
                >
                  {latestValidationStatus === 'success' ? (
                    <>
                      <i className="fas fa-check-circle"></i> Validation OK
                    </>
                  ) : latestValidationStatus === 'Failed Validation' ? (
                    <>
                      <i className="fas fa-exclamation-triangle"></i> Failed Validation
                    </>
                  ) : (
                    <>
                      <i className="fas fa-info-circle"></i> {latestValidationStatus}
                    </>
                  )}
                </span>
              )}
            </div>
            {latestInboundRequest && (
              <small className="text-muted">
                Last updated: {formatTime(capturedPayloads[0]?.timestamp)}
              </small>
            )}
          </div>
        </div>
        <div className="card-body">
          {!latestInboundRequest ? (
            <div className="text-center text-muted py-5">
              <i className="fas fa-hourglass-half fa-3x mb-3"></i>
              <p>Waiting for agent requests...</p>
              <small>Make a request to this channel to see the payload here in real-time.</small>
            </div>
          ) : (
            <div>
              {latestValidationError && (
                <div className="alert alert-danger mb-3">
                  <h6><i className="fas fa-exclamation-circle"></i> Validation Error</h6>
                  <small className="font-monospace">{latestValidationError}</small>
                </div>
              )}
              {/* Vertical 4-stage flow layout */}
              <div className="d-flex flex-column gap-3">
                {/* Source Agent (Top) */}
                <div>
                  <h6 className="mb-2 text-secondary">
                    <i className="fas fa-user"></i> Source Agent
                  </h6>
                  <small className="text-muted d-block mb-2">
                    The agent/client initiating the request
                  </small>
                  <div className="schema-property-card border-secondary bg-light">
                    <div className="p-3 text-center text-muted">
                      <i className="fas fa-robot fa-2x mb-2"></i>
                      <div><strong>External Agent</strong></div>
                      <small>Sends request to gateway</small>
                    </div>
                  </div>
                </div>
                
                {/* Arrow */}
                <div className="text-center">
                  <i className="fas fa-arrow-down fa-2x text-muted"></i>
                </div>
                
                {/* Stage 1: Inbound Request */}
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 text-primary">
                      <i className="fas fa-arrow-down"></i> 1. Inbound Request (from source agent)
                    </h6>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        const payload = JSON.parse(latestInboundRequest);
                        generateAndShowSchema(payload, 'Inbound Request Schema');
                      }}
                      disabled={!latestInboundRequest}
                      title="Generate JSON schema from this payload"
                    >
                      <i className="fas fa-file-code"></i> Generate Schema
                    </button>
                  </div>
                  <small className="text-muted d-block mb-2">
                    Original payload received from the calling agent
                  </small>
                  <div className={`schema-property-card ${
                    latestValidationStatus === 'success' ? 'border-success' : 
                    latestValidationStatus === 'Failed Validation' ? 'border-danger' :
                    'border-info'
                  }`}>
                    <textarea 
                      className={`form-control schema-raw-json ${
                        latestValidationStatus === 'success' ? 'is-valid' : 
                        latestValidationStatus === 'Failed Validation' ? 'is-invalid' :
                        ''
                      }`}
                      style={{ minHeight: '300px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                      value={latestInboundRequest}
                      readOnly
                      placeholder="Inbound request payload will appear here..."
                    />
                  </div>
                </div>
                
                {/* Arrow */}
                {latestOutboundRequest && (
                  <div className="text-center">
                    <i className="fas fa-arrow-down fa-2x text-muted"></i>
                    <small className="d-block text-muted">Gateway processes & transforms</small>
                  </div>
                )}
                
                {/* Stage 2: Outbound Request */}
                {latestOutboundRequest && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 text-info">
                        <i className="fas fa-arrow-up"></i> 2. Outbound (to target)
                      </h6>
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => {
                          const payload = JSON.parse(latestOutboundRequest);
                          generateAndShowSchema(payload, 'Outbound Request Schema');
                        }}
                        disabled={!latestOutboundRequest}
                        title="Generate JSON schema from this payload"
                      >
                        <i className="fas fa-file-code"></i> Generate Schema
                      </button>
                    </div>
                    <small className="text-muted d-block mb-2">
                      Transformed payload sent to target (includes VP, custom metadata, etc.)
                    </small>
                    <div className="schema-property-card border-info">
                      <textarea 
                        className="form-control schema-raw-json"
                        style={{ minHeight: '300px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                        value={latestOutboundRequest}
                        readOnly
                        placeholder="Outbound request payload will appear here..."
                      />
                    </div>
                  </div>
                )}
                
                {/* Arrow */}
                <div className="text-center">
                  <i className="fas fa-arrow-down fa-2x text-muted"></i>
                  <small className="d-block text-muted">Sent to target</small>
                </div>
                
                {/* Target - Fabric Gateway or Direct URL */}
                <div>
                  {isFabricChannel ? (
                    <>
                      <h6 className="mb-2 text-purple">
                        <i className="fas fa-network-wired"></i> Fabric Gateway
                      </h6>
                      <small className="text-muted d-block mb-2">
                        Request forwarded through gateway fabric protocol
                      </small>
                      <div className="schema-property-card border-purple bg-light">
                        <div className="p-3 text-center">
                          <i className="fas fa-project-diagram fa-2x mb-2 text-purple"></i>
                          <div><strong>Target:</strong> <code>{channel?.target_endpoint}</code></div>
                          <small className="text-muted">DIDComm ForwardRequest protocol</small>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h6 className="mb-2 text-dark">
                        <i className="fas fa-globe"></i> Direct URL
                      </h6>
                      <small className="text-muted d-block mb-2">
                        Request sent directly to HTTP endpoint
                      </small>
                      <div className="schema-property-card border-dark bg-light">
                        <div className="p-3 text-center">
                          <i className="fas fa-link fa-2x mb-2 text-dark"></i>
                          <div><strong>Endpoint:</strong> <code>{channel?.target_endpoint}</code></div>
                          <small className="text-muted">Direct HTTP/HTTPS connection</small>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Arrow */}
                {latestInboundResponse && (
                  <div className="text-center">
                    <i className="fas fa-arrow-down fa-2x text-muted"></i>
                    <small className="d-block text-muted">Target responds</small>
                  </div>
                )}
                
                {/* Stage 3: Inbound Response */}
                {latestInboundResponse && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 text-warning">
                        <i className="fas fa-arrow-down"></i> 3. Inbound Response (from target)
                      </h6>
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => {
                          const payload = JSON.parse(latestInboundResponse);
                          generateAndShowSchema(payload, 'Inbound Response Schema');
                        }}
                        disabled={!latestInboundResponse}
                        title="Generate JSON schema from this payload"
                      >
                        <i className="fas fa-file-code"></i> Generate Schema
                      </button>
                    </div>
                    <small className="text-muted d-block mb-2">
                      Raw response received from the target endpoint
                    </small>
                    <div className="schema-property-card border-warning">
                      <textarea 
                        className="form-control schema-raw-json"
                        style={{ minHeight: '300px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                        value={latestInboundResponse}
                        readOnly
                        placeholder="Inbound response payload will appear here..."
                      />
                    </div>
                  </div>
                )}
                
                {/* Arrow */}
                {latestOutboundResponse && (
                  <div className="text-center">
                    <i className="fas fa-arrow-down fa-2x text-muted"></i>
                    <small className="d-block text-muted">Gateway processes response</small>
                  </div>
                )}
                
                {/* Stage 4: Outbound Response */}
                {latestOutboundResponse && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 text-success">
                        <i className="fas fa-arrow-up"></i> 4. Outbound Result (to source agent)
                      </h6>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => {
                          const payload = JSON.parse(latestOutboundResponse);
                          generateAndShowSchema(payload, 'Outbound Response Schema');
                        }}
                        disabled={!latestOutboundResponse}
                        title="Generate JSON schema from this payload"
                      >
                        <i className="fas fa-file-code"></i> Generate Schema
                      </button>
                    </div>
                    <small className="text-muted d-block mb-2">
                      Final response returned to the calling agent
                    </small>
                    <div className="schema-property-card border-success">
                      <textarea 
                        className="form-control schema-raw-json"
                        style={{ minHeight: '300px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                        value={latestOutboundResponse}
                        readOnly
                        placeholder="Outbound response payload will appear here..."
                      />
                    </div>
                  </div>
                )}
                
                {/* Arrow */}
                {latestOutboundResponse && (
                  <div className="text-center">
                    <i className="fas fa-arrow-down fa-2x text-muted"></i>
                  </div>
                )}
                
                {/* Source Agent (Bottom) */}
                {latestOutboundResponse && (
                  <div>
                    <h6 className="mb-2 text-secondary">
                      <i className="fas fa-user"></i> Source Agent
                    </h6>
                    <small className="text-muted d-block mb-2">
                      The agent/client receives the response
                    </small>
                    <div className="schema-property-card border-secondary bg-light">
                      <div className="p-3 text-center text-muted">
                        <i className="fas fa-robot fa-2x mb-2"></i>
                        <div><strong>External Agent</strong></div>
                        <small>Receives response from gateway</small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payload History */}
      {capturedPayloads.length > 0 && (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-info">
              <i className="fas fa-history"></i> Payload History
              <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{capturedPayloads.length}/10</span>
              <small className="text-muted ml-2">Click any row to load into view above</small>
            </h6>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Channel</th>
                    <th>Validation Status</th>
                    <th>Payload Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {capturedPayloads.map((capture, index) => (
                    <tr 
                      key={index} 
                      onClick={() => loadCaptureIntoView(capture)}
                      className="capture-row"
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="timestamp-cell">
                        {formatDateTime(capture.timestamp, true)}
                      </td>
                      <td>
                        <span className="badge badge-secondary">{capture.channel}</span>
                      </td>
                      <td>
                        <span className={`badge ${
                          capture.validation_status === 'success' ? 'badge-success' : 
                          capture.validation_status === 'Failed Validation' ? 'badge-danger' :
                          capture.validation_status === 'method_not_allowed' ? 'badge-warning' : 
                          capture.validation_status === 'mcp_error' ? 'badge-warning' :
                          'badge-info'
                        }`}>
                          {capture.validation_status === 'success' ? (
                            <>
                              <i className="fas fa-check-circle"></i> Success
                            </>
                          ) : capture.validation_status === 'Failed Validation' ? (
                            <>
                              <i className="fas fa-times-circle"></i> Failed Validation
                            </>
                          ) : capture.validation_status === 'method_not_allowed' ? (
                            <>
                              <i className="fas fa-exclamation-triangle"></i> Method Not Allowed
                            </>
                          ) : capture.validation_status === 'mcp_error' ? (
                            <>
                              <i className="fas fa-exclamation-circle"></i> MCP Error
                            </>
                          ) : (
                            <>
                              <i className="fas fa-info-circle"></i> {capture.validation_status}
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        <code className="payload-preview">
                          {JSON.stringify(capture.payload).substring(0, 100)}
                          {JSON.stringify(capture.payload).length > 100 && '...'}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Capture Details Modal */}
      {showCaptureModal && selectedCapture && (
        <>
          <div className="modal fade show modal-show modal-z-high" tabIndex={-1}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content modal-content-flex">
                <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-eye"></i> Captured Payload Details
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={closeCaptureModal}
                >
                  <span>&times;</span>
                </button>
                </div>
                <div className="modal-body modal-body-flex">
                <div className="row mb-3">
                  <div className="col-md-4">
                    <strong>Channel:</strong> 
                    <span className="badge badge-secondary ml-2">{selectedCapture.channel}</span>
                  </div>
                  <div className="col-md-8">
                    <strong>Timestamp:</strong> {formatDateTime(selectedCapture.timestamp, true)}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-12">
                    <strong>Validation Status:</strong> 
                    <span className={`badge ml-2 ${
                      selectedCapture.validation_status === 'success' ? 'badge-success' : 
                      selectedCapture.validation_status === 'method_not_allowed' ? 'badge-warning' : 
                      selectedCapture.validation_status === 'mcp_error' ? 'badge-warning' :
                      'badge-danger'
                    }`}>
                      {selectedCapture.validation_status === 'success' ? (
                        <>
                          <i className="fas fa-check-circle"></i> Success
                        </>
                      ) : selectedCapture.validation_status === 'method_not_allowed' ? (
                        <>
                          <i className="fas fa-exclamation-triangle"></i> Method Not Allowed
                        </>
                      ) : selectedCapture.validation_status === 'mcp_error' ? (
                        <>
                          <i className="fas fa-exclamation-circle"></i> MCP Error
                        </>
                      ) : (
                        <>
                          <i className="fas fa-exclamation-triangle"></i> Failed Validation
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {selectedCapture.validation_error && (
                  <div className="mb-3">
                    <label className="form-label fw-bold text-danger">
                      <i className="fas fa-exclamation-circle"></i> Validation Error Details
                    </label>
                    <div className="alert alert-danger">
                      <small className="font-monospace">{selectedCapture.validation_error}</small>
                    </div>
                  </div>
                )}
                
                <div className="modal-json-container">
                  <div className="row">
                    <div className={selectedCapture.response_payload ? "col-md-6" : "col-md-12"}>
                      <label className="form-label fw-bold mb-2">
                        <i className="fas fa-arrow-right text-primary"></i> Request Payload JSON
                      </label>
                      <div className="schema-property-card border-info d-flex flex-column modal-json-card">
                        <textarea 
                          className="form-control schema-raw-json payload-textarea modal-json-textarea"
                          value={JSON.stringify(selectedCapture.payload, null, 2)}
                          readOnly
                          placeholder="Request payload content will appear here..."
                        />
                      </div>
                    </div>
                    {selectedCapture.response_payload && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold mb-2">
                          <i className="fas fa-arrow-left text-success"></i> Response Payload JSON
                        </label>
                        <div className="schema-property-card border-success d-flex flex-column modal-json-card">
                          <textarea 
                            className="form-control schema-raw-json payload-textarea modal-json-textarea"
                            value={JSON.stringify(selectedCapture.response_payload, null, 2)}
                            readOnly
                            placeholder="Response payload content will appear here..."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-outline-secondary mr-2"
                    onClick={async () => {
                      try {
                        const textToCopy = selectedCapture.response_payload
                          ? `Request:\n${JSON.stringify(selectedCapture.payload, null, 2)}\n\nResponse:\n${JSON.stringify(selectedCapture.response_payload, null, 2)}`
                          : JSON.stringify(selectedCapture.payload, null, 2);
                        await navigator.clipboard.writeText(textToCopy);
                      } catch (error) {
                        console.error('Failed to copy to clipboard:', error);
                      }
                    }}
                  >
                    <i className="fas fa-copy"></i> Copy to Clipboard
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={closeCaptureModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show modal-backdrop-z"></div>
        </>
      )}

      {/* Schema Generation Modal */}
      {showSchemaModal && (
        <>
          <div className="modal fade show modal-show modal-z-high" tabIndex={-1}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content modal-content-flex">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-file-code"></i> {schemaTitle}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={closeSchemaModal}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body modal-body-flex">
                  <div className="mb-3">
                    <p className="text-muted">
                      This JSON schema was automatically derived from the payload structure. 
                      You can use this schema for validation or documentation purposes.
                    </p>
                  </div>
                  
                  <div className="modal-json-container">
                    <label className="form-label fw-bold mb-2">
                      <i className="fas fa-code"></i> Generated JSON Schema
                    </label>
                    <div className="schema-property-card border-primary d-flex flex-column modal-json-card">
                      <textarea 
                        className="form-control schema-raw-json payload-textarea modal-json-textarea"
                        style={{ minHeight: '500px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
                        value={generatedSchema}
                        readOnly
                        placeholder="Generated schema will appear here..."
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-outline-primary mr-2"
                    onClick={async () => {
                      try {
                        const agentIdentitySchema = extractAgentIdentitySchema(schemaSourcePayload, identityFieldName);
                        await navigator.clipboard.writeText(agentIdentitySchema);
                      } catch (error) {
                        console.error('Failed to copy to clipboard:', error);
                      }
                    }}
                    title={`Copy only the ${identityFieldName} schema in channel configuration format`}
                  >
                    <i className="fas fa-user-tag"></i> Copy {identityFieldName} Schema to Clipboard
                  </button>
                  <button 
                    className="btn btn-outline-secondary mr-2"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(generatedSchema);
                      } catch (error) {
                        console.error('Failed to copy to clipboard:', error);
                      }
                    }}
                    title="Copy the complete generated schema"
                  >
                    <i className="fas fa-copy"></i> Copy All to Clipboard
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={closeSchemaModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show modal-backdrop-z"></div>
        </>
      )}

    </div>
  );
};

export default ChannelCapturePage;